import React, { useMemo } from 'react';
import styled from 'styled-components';

import { SHELL_PADDING } from 'components/Studio/CardChrome';
import { DateScope } from 'components/Studio/dateSettings';
import {
  FilterScope,
  InheritedCanvasFilters,
} from 'components/Studio/filterSettings';
import {
  DEFAULT_TEXT_COMPARISON,
  DynamicStudioText,
} from 'components/Studio/StudioText';
import type { TextVerticalAlign } from 'components/Studio/studioTextTypes';
import useRequestMetrics from 'hooks/useRequestMetrics';

const noop = () => {};

const NO_SETTINGS: Studio.TextStudioSettings = {};

// Inset as in Studio's block (rich text has Quill's editor padding)
const MarkdownInset = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: ${SHELL_PADDING.y}px ${SHELL_PADDING.x}px;
`;

// A Text card Studio saved, with its {placeholders} showing the values of its
// metrics and their changes, over its own dates, filters and comparison as
// in Studio, as rich text or Markdown. Its body loads after the dashboard or
// report's metrics gate, so it asks for its metrics itself.
const StudioTextBody = ({
  id,
  text,
  format,
  verticalAlign,
  studio = NO_SETTINGS,
}: {
  id: string;
  text: string;
  format: Studio.TextFormat | undefined;
  // Where the text sits (rich text is also placed by TextGadget's Quill
  // styles)
  verticalAlign: TextVerticalAlign;
  // Absent for Markdown with no metrics
  studio?: Studio.TextStudioSettings;
}) => {
  const metricIds = useMemo(() => studio.metricIds ?? [], [studio.metricIds]);
  useRequestMetrics({ metricIds });

  const content = (
    <DynamicStudioText
      id={id}
      text={text}
      metricIds={metricIds}
      isEditing={false}
      verticalAlign={verticalAlign}
      format={format}
      comparison={studio.comparison ?? DEFAULT_TEXT_COMPARISON}
      onChange={noop}
    />
  );
  return (
    <InheritedCanvasFilters>
      <DateScope dateSettings={studio.dateSettings}>
        <FilterScope filters={studio.filters ?? []}>
          {format === 'markdown' ? (
            <MarkdownInset>{content}</MarkdownInset>
          ) : (
            content
          )}
        </FilterScope>
      </DateScope>
    </InheritedCanvasFilters>
  );
};

export default StudioTextBody;
